import { ref, computed, watch } from "vue";
import api from "@/api/api";
import { useAuthStore } from "@/state/pinia/auth";

// Module-level cache so multiple components on the same page share one fetch
// per shop. We don't bother with TTL — perms only change when an admin edits
// the role, and the page reloads after that.
const cache = new Map(); // shopUuid -> { is_workspace_admin, permissions }
const inflight = new Map(); // shopUuid -> Promise

async function fetchForShop(shopUuid) {
  if (cache.has(shopUuid)) return cache.get(shopUuid);
  if (inflight.has(shopUuid)) return inflight.get(shopUuid);

  const promise = api.get(`/api/shops/${shopUuid}/me/permissions`)
    .then((res) => {
      const data = res.data?.data || { permissions: [], is_workspace_admin: false };
      cache.set(shopUuid, data);
      return data;
    })
    .catch(() => {
      // 403 = no access at all. Treat as empty perms — FE will hide everything.
      const empty = { permissions: [], is_workspace_admin: false };
      cache.set(shopUuid, empty);
      return empty;
    })
    .finally(() => {
      inflight.delete(shopUuid);
    });

  inflight.set(shopUuid, promise);
  return promise;
}

/**
 * Imperative one-shot check, for places without a component instance — router
 * guards in particular. Shares the same module-level cache as the composable.
 */
export async function hasShopPermission(shopUuid, permission) {
  if (!shopUuid) return false;
  const data = await fetchForShop(shopUuid);
  if (data.is_workspace_admin === true) return true;
  const perms = data.permissions || [];
  return perms.includes("*") || perms.includes(permission);
}

/**
 * Check if the current user has shop-scoped permissions. Use in components
 * that need to gate buttons / sections on the active shop.
 *
 *   const { can, isWorkspaceAdmin } = useShopPermissions();
 *   <button v-if="can('shop.printers.pair')">…</button>
 */
export function useShopPermissions(shopUuidOverride = null) {
  const auth = useAuthStore();
  const data = ref({ is_workspace_admin: false, permissions: [] });

  const shopUuid = computed(() => shopUuidOverride || auth.getActiveShop());

  const reload = async () => {
    const uuid = shopUuid.value;
    if (!uuid) {
      data.value = { is_workspace_admin: false, permissions: [] };
      return;
    }
    data.value = await fetchForShop(uuid);
  };

  // Auto-fetch on mount and when active shop changes.
  watch(shopUuid, reload, { immediate: true });

  const isWorkspaceAdmin = computed(() => data.value.is_workspace_admin === true);

  const can = (permission) => {
    if (isWorkspaceAdmin.value) return true;
    const perms = data.value.permissions || [];
    return perms.includes("*") || perms.includes(permission);
  };

  const canAny = (permissions) => permissions.some((p) => can(p));

  const refresh = async () => {
    if (shopUuid.value) cache.delete(shopUuid.value);
    await reload();
  };

  return {
    can,
    canAny,
    isWorkspaceAdmin,
    permissions: computed(() => data.value.permissions),
    refresh,
  };
}
